import type {
  CityAddressType,
  StationKind,
} from '@/state/types';

/**
 * Phase 10.1 — Polizei-Wachen-Tier-Tabellen.
 *
 * Strukturanalog zu `data/stations.ts` (FF/BF/EMS/Hospital), aber separat
 * gehalten weil die Wachen-Hierarchie inhaltlich unabhängig ist:
 *  - Polizeirevier (Landespolizei, 5 Stufen)
 *  - Bereitschaftspolizei (3 Stufen, höhere Kosten, WaWe-fähig)
 *  - SEK-Basis (2 Stufen, sehr teuer, lange Alarmierung)
 *  - Bundespolizei (4 Stufen, Spezialgebiete)
 */

export interface PoliceStationTier {
  level: number;
  cost: number;
  buildTimeHours: number;
  upkeepPerDay: number;
  vehicleSlots: number;
  /** Standard-Wachzellen-Anzahl der Stufe (vor Erweiterungen). */
  holdingCells?: number;
}

export const POLICE_REVIER_TIERS: PoliceStationTier[] = [
  { level: 1, cost: 800_000, buildTimeHours: 6, upkeepPerDay: 400, vehicleSlots: 3, holdingCells: 2 },
  { level: 2, cost: 400_000, buildTimeHours: 12, upkeepPerDay: 550, vehicleSlots: 5, holdingCells: 3 },
  { level: 3, cost: 700_000, buildTimeHours: 24, upkeepPerDay: 750, vehicleSlots: 8, holdingCells: 4 },
  { level: 4, cost: 1_200_000, buildTimeHours: 48, upkeepPerDay: 1_000, vehicleSlots: 12, holdingCells: 6 },
  { level: 5, cost: 1_800_000, buildTimeHours: 72, upkeepPerDay: 1_400, vehicleSlots: 18, holdingCells: 8 },
];

export const RIOT_POLICE_TIERS: PoliceStationTier[] = [
  { level: 1, cost: 3_500_000, buildTimeHours: 24, upkeepPerDay: 1_800, vehicleSlots: 6 },
  { level: 2, cost: 1_500_000, buildTimeHours: 48, upkeepPerDay: 2_400, vehicleSlots: 10 },
  { level: 3, cost: 2_200_000, buildTimeHours: 96, upkeepPerDay: 3_200, vehicleSlots: 15 },
];

export const SEK_BASE_TIERS: PoliceStationTier[] = [
  { level: 1, cost: 6_000_000, buildTimeHours: 96, upkeepPerDay: 4_000, vehicleSlots: 4 },
  { level: 2, cost: 3_500_000, buildTimeHours: 168, upkeepPerDay: 5_500, vehicleSlots: 8 },
];

export const FEDERAL_POLICE_TIERS: PoliceStationTier[] = [
  { level: 1, cost: 4_500_000, buildTimeHours: 24, upkeepPerDay: 2_200, vehicleSlots: 5 },
  { level: 2, cost: 1_800_000, buildTimeHours: 48, upkeepPerDay: 2_900, vehicleSlots: 8 },
  { level: 3, cost: 2_500_000, buildTimeHours: 96, upkeepPerDay: 3_600, vehicleSlots: 12 },
  { level: 4, cost: 3_500_000, buildTimeHours: 168, upkeepPerDay: 4_500, vehicleSlots: 16 },
];

const POLICE_TIERS: Record<StationKind, PoliceStationTier[]> = {
  'police-station': POLICE_REVIER_TIERS,
  'riot-police-station': RIOT_POLICE_TIERS,
  'sek-base': SEK_BASE_TIERS,
  'federal-police-station': FEDERAL_POLICE_TIERS,
  // Non-police kinds — empty arrays, looked up via the FF/BF tables elsewhere.
  'bf-station': [],
  'ff-station': [],
  'ems-station': [],
  hospital: [],
  courthouse: [],
};

export function isPoliceStationKind(kind: StationKind): boolean {
  return (
    kind === 'police-station' ||
    kind === 'riot-police-station' ||
    kind === 'sek-base' ||
    kind === 'federal-police-station'
  );
}

/** Tier rows for a given police station kind. Empty for non-police. */
export function getPoliceTiers(kind: StationKind): PoliceStationTier[] {
  return POLICE_TIERS[kind] ?? [];
}

/** Look up a specific tier; clamps level to the available range. */
export function getPoliceTier(kind: StationKind, level: number): PoliceStationTier | null {
  const list = getPoliceTiers(kind);
  if (list.length === 0) return null;
  return list[Math.max(0, Math.min(list.length - 1, level - 1))];
}

/* ──────────── Heuristic OSM classification ──────────── */

/**
 * Phase 10.1.4 — Classify an OSM `amenity=police` node based on the name tag.
 * Falls back to police-station for normal cases. Tighter rules first so the
 * keyword soup can't match the wrong category.
 */
export function classifyPoliceStation(
  name: string | undefined,
  cityAddressType?: CityAddressType,
): StationKind {
  const n = (name ?? '').toLowerCase();
  if (
    n.includes('bundespolizei') ||
    /\bbpol\b/.test(n) ||
    n.includes('flughafen') ||
    n.includes('hauptbahnhof')
  ) {
    return 'federal-police-station';
  }
  if (n.includes('sek') || n.includes('spezialeinsatzkommando')) {
    return 'sek-base';
  }
  if (n.includes('bereitschaftspolizei') || n.includes('bepo')) {
    return 'riot-police-station';
  }
  // Default — Landespolizei revier.
  // (cityAddressType currently doesn't influence police classification, but kept
  // in the signature for symmetry with classifyFireStation.)
  void cityAddressType;
  return 'police-station';
}

/* ──────────── Min-coverage spec ──────────── */

/** Phase 10.1.4 — minimum NPC police presence per city. */
export const POLICE_MIN_REVIER = 5;
export const POLICE_MIN_FEDERAL = 1;
export const POLICE_MIN_RIOT = 0;
export const POLICE_MIN_SEK = 0;

/**
 * Acquire-cost discount for NPC police stations. We reuse the 70 % rate from
 * NpcStation (Phase 7.2.4) so the player picks up the rules already learned.
 */
export const POLICE_ACQUIRE_DISCOUNT = 0.7;
